import {
  forceRefetchCachedGetTeamByTeamId,
  getCachedTeamJoinPendingState,
  invalidateCachedTeamDetail,
  patchCachedGetTeamByTeamId,
} from '../api/teamCacheUtils'

function readTeamId(payload: Record<string, unknown>): number | null {
  const team = payload.team
  if (!team || typeof team !== 'object') return null
  const id = (team as Record<string, unknown>).id
  if (typeof id === 'number' && id > 0) return id
  if (typeof id === 'string' && id !== '') {
    const parsed = Number(id)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
  }
  return null
}

function readJoinRequestId(payload: Record<string, unknown>): number | null {
  const joinRequest = payload.join_request
  if (!joinRequest || typeof joinRequest !== 'object') return null
  const id = (joinRequest as Record<string, unknown>).id
  if (typeof id === 'number' && id > 0) return id
  if (typeof id === 'string' && id !== '') {
    const parsed = Number(id)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
  }
  return null
}

function readNestedStatus(payload: Record<string, unknown>, key: string): string | null {
  const nested = payload[key]
  if (!nested || typeof nested !== 'object') return null
  const status = (nested as Record<string, unknown>).status
  return typeof status === 'string' ? status : null
}

function shouldApplyJoinRequestDecision(
  getState: () => Record<string, unknown>,
  teamId: number,
  joinRequestId: number | null,
): boolean {
  const pending = getCachedTeamJoinPendingState(getState, teamId)
  if (!pending.hasPendingJoinRequest) return false
  if (pending.pendingJoinRequestId == null || joinRequestId == null) return true
  return pending.pendingJoinRequestId === joinRequestId
}

function applyTeamJoinRequestDecidedSideEffect(
  dispatch: (action: unknown) => unknown,
  getState: () => Record<string, unknown>,
  teamId: number,
  status: string | null,
  joinRequestId: number | null,
): void {
  if (status !== 'approved' && status !== 'rejected') {
    invalidateCachedTeamDetail(dispatch, teamId)
    return
  }

  if (!shouldApplyJoinRequestDecision(getState, teamId, joinRequestId)) {
    return
  }

  if (status === 'rejected') {
    patchCachedGetTeamByTeamId(dispatch, getState, teamId, (draft) => {
      draft.hasPendingJoinRequest = false
      draft.pendingJoinRequestId = undefined
    })
    forceRefetchCachedGetTeamByTeamId(dispatch, getState, teamId)
    return
  }

  patchCachedGetTeamByTeamId(dispatch, getState, teamId, (draft) => {
    if (!draft.isJoined) {
      draft.participants = Number(draft.participants ?? 0) + 1
    }
    draft.isJoined = true
    draft.hasPendingJoinRequest = false
    draft.pendingJoinRequestId = undefined
  })
  invalidateCachedTeamDetail(dispatch, teamId)
}

export function handleNotificationEntitySideEffects(
  dispatch: (action: unknown) => unknown,
  getState: () => Record<string, unknown>,
  notification: unknown,
): void {
  if (!notification || typeof notification !== 'object') return

  const record = notification as Record<string, unknown>
  const type = typeof record.type === 'string' ? record.type : ''
  const payload =
    record.payload && typeof record.payload === 'object'
      ? (record.payload as Record<string, unknown>)
      : null

  if (!type || !payload) return

  const teamId = readTeamId(payload)
  if (teamId == null) return

  if (type === 'team.join_request.decided') {
    applyTeamJoinRequestDecidedSideEffect(
      dispatch,
      getState,
      teamId,
      readNestedStatus(payload, 'join_request'),
      readJoinRequestId(payload),
    )
    return
  }

  if (type === 'team.invitation.decided') {
    if (readNestedStatus(payload, 'invitation') === 'accepted') {
      invalidateCachedTeamDetail(dispatch, teamId)
    }
    return
  }

  if (type === 'team.member.removed' || type === 'team.member.removed.broadcast') {
    invalidateCachedTeamDetail(dispatch, teamId)
  }
}
